import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from "react";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import TableData from "components/ui/TableData";
import { createColumnHelper } from "@tanstack/react-table";
import { Box, Flex, Image } from "theme-ui";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getInvoices, updateInvoice } from "api/Invoices";
import { trackActivity } from "api/ActivityTracker";
import moment from "moment-timezone";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import { EllipsisIcon, QuestionMarkIcon, TrashIcon } from "components/svg";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import UserContext from "context/User";
import SelectInput from "components/forms/SelectInput";
import InvoiceStatusBadge from "components/ui/InvoiceStatusBadge";

const Invoices = () => {
  const columnHelper = createColumnHelper();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const { user } = useContext<any>(UserContext);

  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [confirmVoidModalOpen, setConfirmVoidModalOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [canOpenVoidModal, setCanOpenVoidModal] = useState<boolean>(false);
  const [currentQuery, setCurrentQuery] = useState<any>(null);
  const hasTrackedView = useRef(false);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Invoices";
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!currentQuery) return;
    if (hasTrackedView.current) return;

    hasTrackedView.current = true;

    void trackActivity({
      module: "invoices",
      action: "view_invoices_list",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: null,
      entityId: null,
      metadata: {
        page: currentQuery?.pageNum || 1,
        limit: currentQuery?.recordsPer || 15,
        filters: currentQuery?.filters || {},
        sortBy: currentQuery?.sortBy || null,
        sortDir: currentQuery?.sortDir || null,
        search: currentQuery?.search ?? null,
      },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [user, currentQuery]);

  useEffect(() => {
    if (selectedRow && canOpenVoidModal) {
      setConfirmVoidModalOpen(true);
      setCanOpenVoidModal(false);
    }
  }, [selectedRow, canOpenVoidModal, confirmVoidModalOpen]);

  const columns = [
    columnHelper.accessor("sequence", {
      id: "sequence",
      cell: (info: any) => (
        <Box sx={{ fontWeight: 600, cursor: "pointer", color: "#000" }} onClick={() => navigate(`/invoices/${info.row.original.id}`)}>
          {`GMI-${String(info.getValue()).padStart(5, "0")}`}
        </Box>
      ),
      header: () => <span>Invoice #</span>,
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.project.projectName, {
      id: "project.projectName",
      cell: (info: any) => info.getValue(),
      header: () => <span>Project Name</span>,
    }),
     columnHelper.accessor((row: any) => row?.primaryClient?.name, {
      id: "primaryClient.name",
      cell: (info: any) => info.getValue(),
      header: () => <span>{user.role !== null ? 'Primary Client' : 'Primary Contact'}</span>,
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => <InvoiceStatusBadge invoice={info.row.original} />,
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor((row: any) => row?.total, {
      id: "total",
      cell: (info: any) => {
        const value = info.getValue();
        return value?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ?? "$0.00";
      },
      header: () => <span>Invoice Total</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.dueDate, {
      id: "dueDate",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>Due Date</span>,
    }),
    columnHelper.accessor((row: any) => row?.createdAt, {
      id: "createdAt",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>Date Added</span>,
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        const items: { label: string; onClick: () => void; style?: React.CSSProperties }[] = [{ label: "Details", onClick: () => navigate(`/invoices/${info.row.original.id}`) }];

        if (user.role !== null) {
          items.push({ label: "Edit", onClick: () => navigate(`/invoices/${info.row.original.id}/edit`) });
        }

        if (info.row.original.status < 2 && user?.role !== null) {
          items.push({ label: "Void", onClick: () => confirmVoidInvoice(info.row.original), style: { color: "var(--theme-ui-colors-danger)" } });
        }

        return (
          <Box>
            <DropDownMenu toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />} items={items} />
          </Box>
        );
      },
      header: () => null,
      enableSorting: false,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];

  const confirmVoidInvoice = (row: any) => {
    setSelectedRow(row);
    setCanOpenVoidModal(true);
  };

  const _cancelConfirmModal = () => {
    setConfirmVoidModalOpen(false);
    setSelectedRow(null);
    setCanOpenVoidModal(false);
  };

  const _voidInvoice = async () => {
    setSubmitting(true);
    const res = await updateInvoice({ data: { id: selectedRow.id }, update: { status: 4 } });
    if (res.id) {
      setSubmitting(false);
      setConfirmVoidModalOpen(false);
      setRefreshData(!refreshData);
      setSelectedRow(null);
      setCanOpenVoidModal(false);
      toast.add(`Invoice voided.`, "var(--theme-ui-colors-success)");
    } else {
      setSubmitting(false);
      setConfirmVoidModalOpen(false);
      setSelectedRow(null);
      toast.add(`Error voiding invoice.`, "var(--theme-ui-colors-danger)");
    }
  };

const statusOptions = [
  { key: "created", code: "0", label: "Created" },
  { key: "sent", code: "1", label: "Sent" },
  { key: "paid", code: "2", label: "Paid" },
  { key: "partial", code: "3", label: "Partial Paid" },
  { key: "voided", code: "4", label: "Voided" },
];

const statusKeyToCode = statusOptions.reduce<Record<string, string>>((acc, option) => {
  acc[option.key] = option.code;
  return acc;
}, {});

const statusCodeToKey = statusOptions.reduce<Record<string, string>>((acc, option) => {
  acc[option.code] = option.key;
  return acc;
}, {});

const statusAliasMap: Record<string, string> = {
  ...statusKeyToCode,
  "partial-paid": statusKeyToCode.partial,
  partialpaid: statusKeyToCode.partial,
  void: statusKeyToCode.voided,
};

const statusFilterOptions = [
  { value: "", label: "All statuses" },
  ...statusOptions.map((option) => ({ value: option.code, label: option.label })),
];

const statusOptionMap = statusFilterOptions.reduce<Record<string, { value: string; label: string }>>(
  (acc, option) => {
    if (option.value) {
      acc[`${option.value}`] = option as { value: string; label: string };
    }
    return acc;
  },
  {},
);

  const statusParam = searchParams.get("status");
  const normalizedStatus = useMemo(() => {
    if (!statusParam) return [] as string[];

    const tokens = statusParam
      .split(",")
      .map((token) => token.trim().toLowerCase())
      .map((token) => {
        if (statusAliasMap[token] !== undefined) {
          return statusAliasMap[token];
        }

        if (statusCodeToKey[token] !== undefined) {
          return token;
        }

        return "";
      })
      .filter((token) => token !== "");

    return Array.from(new Set(tokens));
  }, [statusParam]);

  const preFilters = useMemo(
    () => (normalizedStatus.length ? { status: `${normalizedStatus.join(",")}:in` } : undefined),
    [normalizedStatus],
  );

  const handleFilterUrlSync = useCallback((filters: Record<string, string>) => {
    const statusFilter = filters.status;
    let query = "";

    if (statusFilter) {
      const codes = statusFilter
        .split(":")[0]
        .split(",")
        .map((code) => code.trim())
        .filter(Boolean);

      if (codes.length) {
        const names: string[] = [];
        codes.forEach((code) => {
          const key = (statusCodeToKey[code] || code).toLowerCase();
          if (!names.includes(key)) {
            names.push(key);
          }
        });

        if (names.length) {
          query = `?status=${names.join(",")}`;
        }
      }
    }

    const nextUrl = `${window.location.pathname}${query}`;
    if (window.location.search !== query) {
      window.history.replaceState(null, "", nextUrl);
    }
  }, []);

  useEffect(() => {
    if (!statusParam && !normalizedStatus.length) return;

    const canonical = normalizedStatus
      .map((code) => statusCodeToKey[code] || code)
      .map((name) => name.toLowerCase())
      .join(",");

    const targetQuery = canonical ? `?status=${canonical}` : "";

    if (window.location.search !== targetQuery) {
      const nextUrl = `${window.location.pathname}${targetQuery}`;
      window.history.replaceState(null, "", nextUrl);
    }
  }, [normalizedStatus, statusParam]);

  const tableFilters = [
    {
      key: "status",
      component: SelectInput,
      condition: "in",
      props: {
        name: "status",
        label: "Status",
        options: statusFilterOptions,
        clearable: true,
        searchable: false,
        multiple: true,
        $customStyles: { minWidth: "220px" },
      },
      convertValue: (value: any) => {
        console.log("status convertValue input", value);
        const normalize = (token: string) => {
          const trimmed = token.trim().toLowerCase();
          const code = statusAliasMap[trimmed] || trimmed;
          return statusOptionMap[code] || null;
        };

        if (Array.isArray(value)) {
          if (value.every((v) => typeof v === "object" && v !== null && "value" in v)) {
            return value;
          }
          const result = value
            .map((token) => normalize(`${token}`))
            .filter(Boolean) as Array<{ value: string; label: string }>;
          console.log("status convertValue array result", result);
          return result;
        }
        if (value === null || typeof value === "undefined" || value === "") return [];
        const str = `${value}`;
        const [valuePart] = str.split(":");
        const result = (valuePart || "")
          .split(",")
          .map((token) => normalize(token))
          .filter(Boolean) as Array<{ value: string; label: string }>;
        console.log("status convertValue string result", result);
        return result;
      },
      updateValue: (val: any) => {
        if (!val) return null;
        const values = Array.isArray(val) ? val : [val];
        const normalized = values
          .map((v) => {
            const token = typeof v === "object" && v !== null && "value" in v ? `${v.value}` : `${v}`;
            const trimmed = token.trim().toLowerCase();
            const code = statusAliasMap[trimmed] || trimmed;
            return statusCodeToKey[code] !== undefined ? code : "";
          })
          .filter((code) => !!code);
        return normalized.length ? normalized.join(",") : null;
      },
      serializeValue: (value: string) => {
        const [valuePart] = `${value}`.split(":");
        if (!valuePart) return "";
        const names = valuePart
          .split(",")
          .map((code) => code.trim())
          .filter(Boolean)
          .map((code) => statusCodeToKey[code] || code)
          .map((name) => name.toLowerCase());
        return names.join(",");
      },
    },
  ];

  return (
    <>
      <Header></Header>
      <Page title="Invoices" subtitle={`Manage invoices for your company.`}>
        <TableData
          api={getInvoices}
          columns={columns}
          refresh={refreshData}
          title={"All Invoices"}
          defaultSortBy="sequence"
          defaultSortDir="desc"
          nothingFoundText="No Invoices found."
          filters={tableFilters}
          preFilters={preFilters}
          onFiltersChange={handleFilterUrlSync}
          returnQuery={setCurrentQuery}
          filtersStyles={{ display: "grid", gap: "16px", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}
        />
      </Page>
      {confirmVoidModalOpen && (
        <BasicModal
          title={`Void Invoice?`}
          cancel_click={() => _cancelConfirmModal()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={() => _voidInvoice()}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <TrashIcon fill={"#fff"} />}
              {"Void invoice"}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to void this invoice?</P>
        </BasicModal>
      )}
    </>
  );
};

export default Invoices;
